import React, { useEffect, useState } from "react";
import OnboardingScreen from "../OnboardingScreen";
import { OnboardingCircleNextButton, OnboardingNextButton } from "../OnboardingButtons";
import { OnboardingAnimatedTick } from "../OnboardingRows";
import { OnboardingTypewriterText } from "../OnboardingFinishLoading";
import PeakHalfOval from "../PeakHalfOval";
import {
  DEMO_PEAK_SCORE,
  PEAK_SCORE_SCALE_MAX,
  HLR_DETAIL,
  SPORTS_SCIENCE_BODY,
  SPORTS_SCIENCE_CITATION,
  SPORTS_SCIENCE_LEAD,
  peakScoreZone,
} from "../onboardingCopy";

interface DifferenceStepProps {
  progress?: number | null;
  onBack: () => void;
  onContinue: () => void;
}

const LAST_PAGE = 2;

const PlainFact: React.FC<{ title: string; detail: string; note?: string; checked: boolean }> = ({ title, detail, note, checked }) => (
  <div className="flex items-start gap-3">
    <div className="pt-0.5"><OnboardingAnimatedTick checked={checked} /></div>
    <div className="flex flex-col gap-1">
      <p className="font-funnel text-[20px] font-semibold text-peak-text">{title}</p>
      <p className="font-geist text-[15px] text-peak-text-secondary">{detail}</p>
      {note && <p className="font-funnel text-[12px] font-medium italic text-peak-text-tertiary">{note}</p>}
    </div>
  </div>
);

/** "Understanding the Peak Readiness Score" — 3 vertically paged education screens. */
const DifferenceStep: React.FC<DifferenceStepProps> = ({ progress = null, onBack, onContinue }) => {
  const [page, setPage] = useState(0);
  const [score, setScore] = useState(DEMO_PEAK_SCORE);
  const [logChecks, setLogChecks] = useState([false, false, false]);
  const zone = peakScoreZone(Math.round(score));

  useEffect(() => {
    setLogChecks([false, false, false]);
    if (page !== LAST_PAGE) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let delay = 0;
    [0, 1, 2].forEach((i) => {
      delay += i === 0 ? 420 : 520;
      timers.push(setTimeout(() => setLogChecks((prev) => prev.map((c, j) => (j === i ? true : c))), delay));
    });
    return () => timers.forEach(clearTimeout);
  }, [page]);

  const handleBack = () => (page > 0 ? setPage(page - 1) : onBack());

  return (
    <OnboardingScreen
      headerBottom={4}
      progress={progress}
      onBack={handleBack}
      footer={
        page < LAST_PAGE ? (
          <div className="flex justify-end">
            <OnboardingCircleNextButton down onClick={() => setPage(page + 1)} />
          </div>
        ) : (
          <OnboardingNextButton label="Choose your plan" onClick={onContinue} />
        )
      }
    >
      <div key={page} className="flex flex-1 flex-col justify-center animate-slide-in">
        {page === 0 && (
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="flex flex-col items-center">
              <p className="font-caveat text-[30px] leading-none text-peak-text-secondary">understanding the</p>
              <h1 className="font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text">Peak Readiness Score</h1>
            </div>
            <PeakHalfOval
              progress={Math.round(score) / PEAK_SCORE_SCALE_MAX}
              color={zone.color}
              animateIn={page === 0}
            >
              <span className="font-geist text-[56px] font-bold leading-none tabular-nums text-peak-text">
                {Math.round(score)}
              </span>
              <span className="font-funnel text-[13px] font-semibold text-peak-text">Peak Readiness</span>
            </PeakHalfOval>
            <div className="flex w-full flex-col gap-2">
              <div className="flex flex-col gap-1">
                <p className="font-funnel text-[20px] font-semibold transition-colors duration-300" style={{ color: zone.color }}>
                  {zone.name}&nbsp;&nbsp;·&nbsp;&nbsp;{zone.range}
                </p>
                <p className="min-h-[44px] font-funnel text-[14px] text-peak-text-secondary">{zone.detail}</p>
              </div>
              <div className="flex flex-col gap-1">
                <input
                  type="range"
                  min={0}
                  max={PEAK_SCORE_SCALE_MAX}
                  step={1}
                  value={score}
                  onChange={(e) => setScore(Number(e.target.value))}
                  aria-label="Explore readiness zones"
                  className="w-full"
                  style={{ accentColor: zone.color }}
                />
                <div className="flex justify-between font-geist text-[11px] text-peak-text-secondary">
                  <span>0</span>
                  <span>Drag to explore your zones</span>
                  <span>{PEAK_SCORE_SCALE_MAX}</span>
                </div>
              </div>
              <OnboardingTypewriterText
                className="pt-3 text-center text-peak-text-secondary"
                isActive={page === 0}
                text="The Peak Score is a guide. We don't tell you what to think, but teach how to think about training decisions."
              />
            </div>
          </div>
        )}

        {page === 1 && (
          <div className="flex flex-col gap-5">
            <h1 className="pl-[3px] font-caveat text-[34px] leading-none text-peak-text">The Sports Science</h1>
            <p className="font-funnel text-[20px] font-semibold leading-snug text-peak-text">{SPORTS_SCIENCE_LEAD}</p>
            <p className="font-funnel text-[18px] leading-snug text-peak-text">{SPORTS_SCIENCE_BODY}</p>
            <p className="font-geist text-[9px] text-peak-text-secondary">{SPORTS_SCIENCE_CITATION}</p>
          </div>
        )}

        {page === LAST_PAGE && (
          <div className="flex flex-col gap-5">
            <h1 className="pl-[3px] font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text">
              What you log <span className="font-caveat text-[38px] font-normal tracking-normal">daily</span>
            </h1>
            <div className="flex flex-col gap-4">
              <PlainFact title="Wellness" detail="Self-reported questionnaire" checked={logChecks[0]} />
              <PlainFact title="Training Load" detail="Perceived Effort x Time" checked={logChecks[1]} />
              <PlainFact
                title="Heart Lung Recovery (HLR)"
                detail={HLR_DETAIL}
                checked={logChecks[2]}
              />
            </div>
          </div>
        )}
      </div>
    </OnboardingScreen>
  );
};

export default DifferenceStep;
